"use strict";

import { isMandatoryScript } from "~/helpers";
import { CONSTANTS } from "~/helpers/constants";

const originalAddEventListener = EventTarget.prototype.addEventListener;
const originalDispatchEvent = EventTarget.prototype.dispatchEvent;

/**
 * Initialize the event blocker to intercept all events
 */
export function initEventBlocker() {
  const pendingListeners = window.cookiedock.__pendingListeners || [];
  const originalWindowAddEventListener = window.cookiedock.__originalWindowAddEventListener || window.addEventListener;

  if (pendingListeners.length > 0) {
    window.cookiedock.__listeners = window.cookiedock.__listeners || {
      analytics: [],
      marketing: [],
      functional: [],
      unknown: [],
    };

    window.cookiedock.__listeners.analytics.push(...pendingListeners);
  }

  overrideDispatchEvent();

  window.cookiedock.__listeners = window.cookiedock.__listeners || {
    analytics: [],
    marketing: [],
    functional: [],
    unknown: [],
  };

  window.cookiedock.__allowed_event = null;

  overrideAddEventListener();

  overrideWindowAddEventListener(originalWindowAddEventListener);

  captureExistingEventHandlers();
}

/**
 * Override the dispatchEvent method to block event dispatching
 */
function overrideDispatchEvent() {
  EventTarget.prototype.dispatchEvent = function (event: Event) {
    if (window.cookiedock.__allowed_event === event.type) {
      window.cookiedock.__allowed_event = null;
      return originalDispatchEvent.call(this, event);
    }

    if (CONSTANTS.EVENTS_TO_TRACK.includes(event.type)) {
      return true;
    }

    return originalDispatchEvent.call(this, event);
  };
}

/**
 * Override the addEventListener method to block event listeners
 */
function overrideAddEventListener() {
  EventTarget.prototype.addEventListener = function (type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions) {
    const { currentScript } = document;

    // Allow events from Cookiedock script or mandatory scripts
    if (currentScript && (currentScript.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(currentScript))) {
      return originalAddEventListener.call(this, type, listener || (() => {}), options);
    }

    if (CONSTANTS.EVENTS_TO_TRACK.includes(type)) {
      let category: string = "analytics";
      if (currentScript && currentScript.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME)) {
        category = currentScript.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME) || "analytics";
      }

      if (!window.cookiedock.__listeners[category]) {
        window.cookiedock.__listeners[category] = [];
      }

      window.cookiedock.__listeners[category]!.push({
        element: this,
        type,
        listener: listener || (() => {}),
        options,
      });

      return undefined;
    }

    return originalAddEventListener.call(this, type, listener || (() => {}), options);
  };
}

/**
 * Override window.addEventListener to block global event listeners
 */
function overrideWindowAddEventListener(originalWindowAddEventListener: typeof window.addEventListener) {
  window.addEventListener = function (type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions) {
    // Allow events from Cookiedock script or mandatory scripts
    if (document.currentScript?.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(document.currentScript || undefined)) {
      return originalWindowAddEventListener.call(this, type, listener || (() => {}), options);
    }

    if (document.currentScript?.id !== CONSTANTS.DEFAULT.SCRIPT_NAME) {
      if (CONSTANTS.EVENTS_TO_TRACK.includes(type)) {
        if (!window.cookiedock.__listeners.analytics) {
          window.cookiedock.__listeners.analytics = [];
        }

        window.cookiedock.__listeners.analytics.push({
          element: window,
          type,
          listener: listener || (() => {}),
          options,
        });

        return undefined;
      }

      return originalWindowAddEventListener.call(this, type, listener || (() => {}), options);
    }

    return originalWindowAddEventListener.call(this, type, listener || (() => {}), options);
  };
}

/**
 * Capture existing event handlers on page load
 */
function captureExistingEventHandlers() {
  try {
    if (window.onload) {
      window.cookiedock.__listeners.analytics.push({
        element: window,
        type: "load",
        listener: window.onload,
      });
      window.onload = null;
    }
  } catch (err) {
    console.error("Error capturing existing event handlers:", err);
  }
}

/**
 * Override additional window methods that can execute code
 */
export function overrideWindowMethods() {
  overrideEval();
  overrideSetTimeout();
  overrideSetInterval();
  overrideFunction();
}

/**
 * Override window.eval to block dynamic code execution
 */
function overrideEval() {
  const originalEval = window.eval;
  window.eval = function (...args) {
    const currentScript = document.currentScript;
    if (currentScript && (currentScript.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(currentScript))) {
      return originalEval.apply(window, args);
    }
    return undefined;
  };
}

/**
 * Override window.setTimeout to block scheduled code execution
 */
function overrideSetTimeout() {
  const originalSetTimeout = window.setTimeout;

  // @ts-expect-error - TS2322: Type 'typeof setTimeout' is not assignable to type 'typeof setTimeout'.
  window.setTimeout = function (handler: TimerHandler, timeout?: number, ...args: any[]): NodeJS.Timeout | number {
    const currentScript = document.currentScript;

    if (currentScript && (currentScript.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(currentScript))) {
      return originalSetTimeout.apply(window, [handler, timeout, ...args] as any);
    }

    if (timeout && timeout > 1000) {
      if (typeof handler === "function") {
        return originalSetTimeout.apply(window, [handler, timeout, ...args] as any);
      }
    }
    return 0;
  };
}

/**
 * Override window.setInterval to block repeating code execution
 */
function overrideSetInterval() {
  const originalSetInterval = window.setInterval;

  // @ts-expect-error - TS2322: Type 'typeof setInterval' is not assignable to type 'typeof setInterval'.
  window.setInterval = function (handler: TimerHandler, timeout?: number, ...args: any[]): NodeJS.Timeout | number {
    const currentScript = document.currentScript;

    if (currentScript && (currentScript.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(currentScript))) {
      return originalSetInterval.apply(window, [handler, timeout, ...args] as any);
    }

    if (timeout && timeout > 3000) {
      if (typeof handler === "function") {
        return originalSetInterval.apply(window, [handler, timeout, ...args] as any);
      }
    }
    return 0;
  };
}

/**
 * Override window.Function to block dynamic function creation
 */
function overrideFunction() {
  const originalFunction = Function;

  // @ts-expect-error - TS2322: Type 'typeof Function' is not assignable to type 'typeof Function'.
  window.Function = function (...args: string[]) {
    const currentScript = document.currentScript;
    if (currentScript && (currentScript.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(currentScript))) {
      return new originalFunction(...args);
    }
    return function () {
      return undefined;
    };
  };
}

/**
 * Activate event listeners for a specific consent category
 */
export function activateEventListeners(category: string) {
  if (!window.cookiedock.__listeners || !window.cookiedock.__listeners[category]) {
    return;
  }

  const listeners = window.cookiedock.__listeners[category];

  const activatedIndices = [];

  for (let i = 0; i < listeners.length; i++) {
    const listener = listeners[i];

    if (!listener?.element || !listener?.type || !listener?.listener) {
      console.warn(`Skipping invalid listener for ${listener?.type || "unknown"}`);
      activatedIndices.push(i);
      continue;
    }

    try {
      const isInDOM = listener.element === window || listener.element === document || document.contains(listener.element as Node);

      if (isInDOM) {
        originalAddEventListener.call(listener.element, listener.type, listener.listener, listener.options);

        if (listener.type === "load" && document.readyState === "complete") {
          try {
            window.cookiedock.__allowed_event = "load";
            const loadEvent = new Event("load");
            listener.element.dispatchEvent(loadEvent);
          } catch (dispatchError) {
            console.error("Error dispatching synthetic load event:", dispatchError);
          }
        }

        if (listener.type === "DOMContentLoaded" && (document.readyState === "complete" || document.readyState === "interactive")) {
          try {
            window.cookiedock.__allowed_event = "DOMContentLoaded";
            const domContentLoadedEvent = new Event("DOMContentLoaded");
            listener.element.dispatchEvent(domContentLoadedEvent);
          } catch (dispatchError) {
            console.error("Error dispatching synthetic DOMContentLoaded event:", dispatchError);
          }
        }

        activatedIndices.push(i);
      } else {
        console.warn(`Element for ${listener.type} event no longer in DOM, skipping`);
        activatedIndices.push(i);
      }
    } catch (error) {
      console.error(`Failed to activate ${listener.type} event listener:`, error);

      activatedIndices.push(i);
    }
  }

  for (let i = activatedIndices.length - 1; i >= 0; i--) {
    listeners.splice(activatedIndices[i]!, 1);
  }
}
